import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { format, startOfMonth } from 'date-fns';

export interface MesFechadoStatus {
  fechado: boolean;
  fechado_em: string | null;
  fechado_por: string | null;
  motivo: string | null;
}

/** Lê o status de fechamento de um mês. Retorna { fechado: true } se a
 *  competência tem registro ativo (sem reabertura) em comissao_mes_fechado. */
export function useMesFechado(competencia: Date) {
  const competenciaIso = format(startOfMonth(competencia), 'yyyy-MM-dd');
  return useQuery({
    queryKey: ['mes-fechado', competenciaIso],
    queryFn: async (): Promise<MesFechadoStatus> => {
      const { data, error } = await supabase
        .from('comissao_mes_fechado' as any)
        .select('competencia, fechado_em, fechado_por, motivo, reaberto_em')
        .eq('competencia', competenciaIso)
        .maybeSingle();
      if (error && (error as any).code !== 'PGRST116') throw error;
      const row = data as any;
      const ativo = row && !row.reaberto_em;
      return {
        fechado: !!ativo,
        fechado_em: ativo ? row.fechado_em : null,
        fechado_por: ativo ? row.fechado_por : null,
        motivo: ativo ? row.motivo : null,
      };
    },
    staleTime: 60 * 1000,
  });
}

/** Fecha o mês — invoca RPC fechar_mes_comissao. Master only (validado server-side).
 *  `resultados` é a fotografia da tela por pessoa (mesmos hooks da Visão),
 *  gravada em snapshot_payload.tela — é o que a tela LÊ quando o mês está fechado. */
export function useFecharMesComissao() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ competencia, motivo, resultados }: { competencia: Date; motivo?: string; resultados?: unknown[] }) => {
      const competenciaIso = format(startOfMonth(competencia), 'yyyy-MM-dd');
      const { data, error } = await supabase.rpc('fechar_mes_comissao', {
        p_competencia: competenciaIso,
        p_motivo: motivo ?? null,
        p_resultados: resultados ?? null,
      });
      if (error) throw error;
      return data as number;
    },
    onSuccess: (n) => {
      queryClient.invalidateQueries({ queryKey: ['mes-fechado'] });
      queryClient.invalidateQueries({ queryKey: ['historico-comissoes'] });
      queryClient.invalidateQueries({ queryKey: ['comissao-snapshot-tela'] });
      toast({
        title: 'Mês fechado',
        description: `${n} consultores snapshotados. Edições futuras não afetam mais este mês.`,
      });
    },
    onError: (e: Error) => {
      toast({ title: 'Erro ao fechar mês', description: e.message, variant: 'destructive' });
    },
  });
}

/** Reabre um mês fechado — exige motivo. Master only. */
export function useReabrirMesComissao() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ competencia, motivo }: { competencia: Date; motivo: string }) => {
      const competenciaIso = format(startOfMonth(competencia), 'yyyy-MM-dd');
      const { error } = await supabase.rpc('reabrir_mes_comissao', {
        p_competencia: competenciaIso,
        p_motivo: motivo,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mes-fechado'] });
      toast({ title: 'Mês reaberto', description: 'Cálculo voltou a ser dinâmico para esta competência.' });
    },
    onError: (e: Error) => {
      toast({ title: 'Erro ao reabrir mês', description: e.message, variant: 'destructive' });
    },
  });
}
